import React, { useContext } from 'react';
import { graphql } from 'gatsby';
import styled from 'styled-components';
import { breakpoints } from '../styles/Variables';
import SEO from '../components/SEO';
import localize from '../components/localize';
import LocaleContext from '../components/LocaleContext';
import { tr } from '../utils/translations';
import Footer from '../components/Footer';
import PropertyTypesPreview from '../components/PropertyTypesPreview';
import PropertiesCarousel from '../components/PropertiesCarousel';
import LocaleLink from '../components/LocaleLink';

const PropertiesPageStyles = styled.div`
  --maxPageWidth: 60rem;
  background-color: var(--offWhite);
  min-height: calc(100vh - var(--navHeight));

  .sections {
    max-width: var(--maxPageWidth);
    margin-left: auto;
    margin-right: auto;
    padding: 4rem 0 2rem 0;

    @media (min-width: ${breakpoints.breakLg}) {
      padding-top: 6rem;
    }
  }

  h1 {
    margin-bottom: 0.5em;
    padding: 0 var(--col1);
    text-align: center;
    font-size: var(--fsTitle2);

    @media (min-width: ${breakpoints.breakMd}) {
      font-size: var(--fsTitle1);
    }
  }

  .button {
    margin-top: 1rem;
    background-color: var(--green);
    color: var(--offWhite);
    font-size: var(--fsBody);
    text-transform: lowercase;

    &:hover {
      background-color: var(--offWhite);
      color: var(--black);
      border-color: var(--black);
    }
  }

  .buttonWrapper {
    text-align: center;
  }

  .intro {
    margin: 0 auto 3em auto;
    padding: 0 var(--col1);
    text-transform: none;
    text-align: center;

    @media (max-width: ${breakpoints.breakSm}) {
      font-size: var(--fsBody);
    }
  }

  .copyAfterIntro {
    margin: 0 auto 6em auto;
    padding: 0 var(--col1);

    h3 {
      text-align: center;
    }

    p {
      text-align: justify;
    }
  }
`;

const PropertyPreviewStyles = styled.div`
  h3 {
    border-bottom: 1px solid var(--black);
    text-transform: none;
    text-align: left;
  }

  .info {
    padding: 2rem var(--col1) 4rem var(--col1);
    text-align: center;

    p {
      text-align: left;
    }
  }

  .subtitle {
    font-size: var(--fsBody);
    display: block;
    font-style: italic;
    padding-bottom: 0.25rem;
  }

  @media (min-width: ${breakpoints.breakLg}) {
    .property {
      display: grid;
      grid-template-columns: 1fr 1fr;
      grid-gap: 2rem;
      margin-bottom: 4rem;
      align-items: center;

      .info {
        text-align: left;
        padding: 0;
      }
    }
  }
`;

const PropertiesPage = ({ data }) => {
  const [locale] = useContext(LocaleContext);
  const { page, allPages } = data;
  const thisPageSlug = page.slug;
  const siteSettings = data.siteSettings.nodes[0];
  const propertyTypeForThisPage = page.className === 'stayNow' ? 'rent' : 'buy';
  const properties = allPages.nodes.filter(
    (property) => property.type === propertyTypeForThisPage
  );

  return (
    <>
      <SEO title={page.metaname} description={page.metaDescription} />
      {page && (
        <PropertiesPageStyles>
          <div className="sections">
            <h1>
              {tr(
                'titles',
                propertyTypeForThisPage === 'buy' ? 'liveAtMusa' : 'stayNow',
                locale
              )}
            </h1>

            <h3 className="intro">
              {tr(
                'notices',
                propertyTypeForThisPage === 'buy'
                  ? 'propertiesPageIntro'
                  : 'stayNowPageIntro',
                locale
              )}
              {propertyTypeForThisPage === 'buy' && (
                <div className="buttonWrapper">
                  <LocaleLink to="/#coming-soon" className="button cta">
                    {tr('cta', 'viewTimeline', locale)}
                  </LocaleLink>
                </div>
              )}
            </h3>

            {/* Properties page (Live at MUSA) copy */}
            {propertyTypeForThisPage === 'buy' && (
              <div className="copyAfterIntro">
                <h3>{tr('titles', 'propertiesPageCopyTitle', locale)}</h3>
                <p>{tr('notices', 'propertiesPageCopy', locale)}</p>
                <div className="buttonWrapper">
                  <a
                    className="button cta"
                    href="mailto:tara@findmusa.mx?cc=marketing@findmusa.mx&subject=Enquiry%20about%20investing"
                  >
                    {tr('cta', 'enquireAboutROI', locale)}
                  </a>
                </div>
              </div>
            )}

            <PropertyTypesPreview
              propertyTypes={properties}
              styles={PropertyPreviewStyles}
            />
          </div>
          <PropertiesCarousel
            pages={allPages}
            showPageType={page.className === 'stayNow' ? 'buy' : 'rent'}
            parentPageSlug={thisPageSlug}
            className="allPages"
            title={tr('titles', 'viewMoreProperties', locale)}
          />
        </PropertiesPageStyles>
      )}
      {siteSettings && <Footer siteSettings={siteSettings} />}
    </>
  );
};
export default localize(PropertiesPage);

export const query = graphql`
  query($pageId: String!, $imageMaxWidth: Int!) {
    siteSettings: allSanitySiteSettings {
      nodes {
        ...SiteSettingsFields
      }
    }
    allPages: allSanityPropertyType(
      filter: { hidden: { ne: true } }
      sort: { fields: order }
    ) {
      nodes {
        id
        name {
          ...LocaleStringFields
        }
        subtitle {
          ...LocaleStringFields
        }
        slug {
          ...LocaleStringFields
        }
        introduction {
          ...LocaleBlockFields
        }
        _rawIntroduction
        image {
          asset {
            fluid(maxWidth: 800) {
              ...GatsbySanityImageFluid
            }
          }
        }
        className
        type
        heroVideoVimeoID
        bookURL
        hidden
      }
    }
    page: sanityCustomPage(id: { eq: $pageId }) {
      id
      className
      slug {
        ...LocaleStringFields
      }
      name {
        ...LocaleStringFields
      }
      metaname {
        ...LocaleStringFields
      }
      metaDescription {
        ...LocaleStringFields
      }
      metaImage {
        asset {
          fluid(maxWidth: $imageMaxWidth) {
            ...GatsbySanityImageFluid
          }
        }
      }
    }
  }
`;
